// frontend/src/components/forms/RegisterForm.js
import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { FaShieldAlt, FaCheckCircle, FaSpinner, FaPhone } from 'react-icons/fa';

function RegisterForm() {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [formError, setFormError] = useState('');

  // OTP verification step
  const [otpMode, setOtpMode] = useState(false);
  const [pinId, setPinId] = useState(null);
  const [otp, setOtp] = useState('');
  const [verifyError, setVerifyError] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);

  const { register, verifyEmailOtp, loading, error: authError, setError } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    setError(null);
    const refCode = searchParams.get('ref');
    if (refCode) {
        setReferralCode(refCode);
    }
  }, [searchParams, setError]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setError(null);

    if (password !== confirmPassword) {
      setFormError('Passwords do not match.');
      return;
    }
    if (password.length < 6) {
        setFormError('Password must be at least 6 characters long.');
        return;
    }

    const userData = {
      email,
      password,
      full_name: fullName || null,
      referral_code: referralCode || null,
    };

    const result = await register(userData);
    if (result.success) {
        setPinId(result.pin_id);
        setOtpMode(true);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setVerifyError('');
    if (otp.length < 4) {
        setVerifyError('Please enter the code we emailed you.');
        return;
    }
    setVerifying(true);
    const result = await verifyEmailOtp(email, pinId, otp);
    setVerifying(false);
    if (result.success) {
        setVerified(true);
        setTimeout(() => navigate('/login'), 2000);
    } else {
        setVerifyError(result.error || 'Invalid or expired code.');
    }
  };

  // --- SUCCESS STATE: VERIFIED ---
  if (verified) {
      return (
        <div className="max-w-md mx-auto mt-10 bg-white p-8 rounded-lg shadow-xl text-center">
            <div className="flex justify-center mb-4">
                <FaCheckCircle className="text-6xl text-green-500" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Email Verified!</h2>
            <p className="text-gray-600 mb-6">
                Your account is now active. Redirecting you to login...
            </p>
            <Link
                to="/login"
                className="inline-block w-full bg-primary hover:bg-primary-dark text-white font-bold py-3 px-4 rounded-md transition duration-300"
            >
                Go to Login
            </Link>
        </div>
      );
  }

  // --- OTP ENTRY STATE ---
  if (otpMode) {
      return (
        <div className="max-w-md mx-auto mt-10 bg-white p-8 rounded-lg shadow-xl text-center">
            <div className="flex justify-center mb-4">
                <FaShieldAlt className="text-6xl text-primary" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Verify Your Email</h2>
            <p className="text-gray-600 mb-6">
                We've emailed a <strong>6-digit code</strong> to <strong>{email}</strong>.
                Enter it below to activate your account. (Check your spam folder too.)
            </p>

            {verifyError && (
                <div className="mb-4 p-3 bg-red-100 text-red-700 rounded text-sm">
                    {verifyError}
                </div>
            )}

            <form onSubmit={handleVerify} className="space-y-4">
                <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:outline-none text-center text-2xl tracking-[0.5em] font-bold"
                    placeholder="------"
                />
                <button
                    type="submit"
                    disabled={verifying}
                    className="w-full bg-primary hover:bg-primary-dark text-white font-bold py-3 px-4 rounded-md transition duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                    {verifying ? <><FaSpinner className="animate-spin" /> Verifying...</> : <><FaCheckCircle /> Verify Account</>}
                </button>
            </form>

            <p className="text-center text-sm text-gray-600 mt-6">
                Entered the wrong email?{' '}
                <button
                    type="button"
                    onClick={() => { setOtpMode(false); setOtp(''); setVerifyError(''); }}
                    className="text-primary hover:underline font-medium"
                >
                    Start over
                </button>
            </p>
        </div>
      );
  }

  // --- REGISTRATION FORM ---
  return (
    <div className="max-w-md mx-auto mt-10 bg-white p-8 rounded-lg shadow-xl">
      <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">Create Account</h2>

      {/* --- PHONE REGISTRATION BUTTON --- */}
      <button
        onClick={() => navigate('/login?mode=phone')}
        className="w-full flex items-center justify-center gap-3 bg-green-600 text-white py-3 rounded-lg font-bold hover:bg-green-700 transition shadow-md mb-6"
      >
        <FaPhone /> Register with Phone Number
      </button>

      <div className="flex items-center justify-center gap-4 mb-6">
          <div className="h-px bg-gray-300 flex-grow"></div>
          <span className="text-xs text-gray-500 font-bold uppercase">OR USE EMAIL</span>
          <div className="h-px bg-gray-300 flex-grow"></div>
      </div>

      {formError && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded text-sm">
          {formError}
        </div>
      )}

      {authError && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded text-sm">
          {authError}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="mb-4">
        <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-1">
            Full Name (Optional)
        </label>
        <input
            type="text"
            id="fullName"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            placeholder="John Doe"
            disabled={loading}
        />
        </div>
        <div className="mb-4">
        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            Email Address <span className="text-red-500">*</span>
        </label>
        <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            placeholder="you@example.com"
            disabled={loading}
        />
        </div>
        <div className="mb-4">
        <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
            Password <span className="text-red-500">*</span>
        </label>
        <input
            type="password"
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength="6"
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            placeholder="••••••••"
            disabled={loading}
        />
        <p className="text-xs text-gray-500 mt-1">Must be at least 6 characters.</p>
        </div>
        <div className="mb-6">
        <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
            Confirm Password <span className="text-red-500">*</span>
        </label>
        <input
            type="password"
            id="confirmPassword"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            minLength="6"
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            placeholder="••••••••"
            disabled={loading}
        />
        </div>

        <div className="mb-6">
        <label htmlFor="referralCode" className="block text-sm font-medium text-gray-700 mb-1">
            Referral Code (Optional)
        </label>
        <input
            type="text"
            id="referralCode"
            value={referralCode}
            onChange={(e) => setReferralCode(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent uppercase"
            placeholder="Enter code if you have one"
            disabled={loading}
        />
        </div>

        <button
        type="submit"
        disabled={loading}
        className={`w-full bg-primary hover:bg-primary-dark text-white font-bold py-3 px-4 rounded-md transition duration-300 ${
            loading ? 'opacity-50 cursor-not-allowed' : ''
        }`}
        >
        {loading ? 'Registering...' : 'Register with Email'}
        </button>
      </form>

      <p className="text-center text-sm text-gray-600 mt-6">
        Already have an account?{' '}
        <Link to="/login" className="text-primary hover:underline font-medium">
          Log in here
        </Link>
      </p>
    </div>
  );
}

export default RegisterForm;